import { useState, useMemo, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Copy, CalendarDays, CheckCircle2, RotateCcw } from "lucide-react";

const HOURS = Array.from({ length: 15 }, (_, i) => i + 7);

export function CapacityPanel({ serviceId, services }: { serviceId: number; services: any[] }) {
  const utils = trpc.useUtils();

  // ── Stable date string (never changes after mount) ──────────────────────
  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  // ── Local UI state ───────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState(today);
  const [copyFromId, setCopyFromId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"rules" | "exceptions">("rules");
  const [localRules, setLocalRules] = useState<Record<number, string>>({});
  const [localExcs, setLocalExcs] = useState<Record<number, string>>({});

  // ── Stable query inputs (useMemo prevents new object on every render) ────
  const rulesInput = useMemo(() => ({ serviceId }), [serviceId]);
  const exceptionsInput = useMemo(
    () => ({ serviceId, exceptionDate: selectedDate }),
    [serviceId, selectedDate]
  );

  // ── Server queries ───────────────────────────────────────────────────────
  const { data: rulesData, isLoading: rulesLoading } = (trpc as any).services.listCapacityRules.useQuery(rulesInput);
  const rules = rulesData ?? [];

  const { data: mySalonData } = trpc.salon.getMySalon.useQuery();
  const salonId = mySalonData?.id;

  const availabilityInput = useMemo(
    () => ({ serviceId, salonId: salonId ?? 0, date: selectedDate }),
    [serviceId, salonId, selectedDate]
  );
  const { data: availabilityData } = (trpc as any).services.getHourlyAvailability.useQuery(
    availabilityInput,
    { enabled: !!salonId, refetchInterval: 30000 }
  );
  const availability = availabilityData ?? [];

  const { data: exceptionsData } = (trpc as any).services.listCapacityExceptions.useQuery(
    exceptionsInput,
    { enabled: activeTab === "exceptions" }
  );
  const exceptions = exceptionsData ?? [];

  // ── Mutations ────────────────────────────────────────────────────────────
  const upsertMutation = (trpc as any).services.upsertCapacityRule.useMutation({
    onSuccess: () => (utils as any).services.listCapacityRules.invalidate({ serviceId }),
    onError: (e: any) => toast.error(e.message),
  });
  const copyMutation = (trpc as any).services.copyCapacityRules.useMutation({
    onSuccess: () => {
      (utils as any).services.listCapacityRules.invalidate({ serviceId });
      toast.success("Regras copiadas com sucesso!");
      setCopyFromId("");
    },
    onError: (e: any) => toast.error(e.message),
  });
  const resetMutation = (trpc as any).services.resetCapacityRules.useMutation({
    onSuccess: () => {
      (utils as any).services.listCapacityRules.invalidate({ serviceId });
      toast.success("Limites resetados para 0!");
    },
    onError: (e: any) => toast.error(e.message),
  });
  const upsertExcMutation = (trpc as any).services.upsertCapacityException.useMutation({
    onSuccess: () => (utils as any).services.listCapacityExceptions.invalidate({ serviceId }),
    onError: (e: any) => toast.error(e.message),
  });
  const deleteExcMutation = (trpc as any).services.deleteCapacityException.useMutation({
    onSuccess: () => (utils as any).services.listCapacityExceptions.invalidate({ serviceId }),
    onError: (e: any) => toast.error(e.message),
  });

  // ── Sync local state when server data arrives ────────────────────────────
  const lastRulesRef = useRef<any>(null);
  const lastExceptionsRef = useRef<any>(null);
  useEffect(() => {
    if (rulesData === lastRulesRef.current) return;
    lastRulesRef.current = rulesData;
    const initial: Record<number, string> = {};
    HOURS.forEach((h) => {
      const rule = (rulesData as any[] | undefined)?.find((r: any) => r.hourSlot === h);
      initial[h] = String(rule?.maxCapacity ?? 0);
    });
    setLocalRules(initial);
  }, [rulesData]);

  useEffect(() => {
    if (exceptionsData === lastExceptionsRef.current) return;
    lastExceptionsRef.current = exceptionsData;
    const initial: Record<number, string> = {};
    HOURS.forEach((h) => {
      const exc = (exceptionsData as any[] | undefined)?.find((e: any) => e.hourSlot === h);
      initial[h] = String(exc?.maxCapacity ?? 0);
    });
    setLocalExcs(initial);
  }, [exceptionsData, selectedDate]);

  // ── Event handlers ───────────────────────────────────────────────────────
  const handleRuleBlur = (hour: number) => {
    const value = localRules[hour] ?? "0";
    const max = parseInt(value, 10);
    if (isNaN(max) || max < 0) {
      const rule = (rules as any[]).find((r: any) => r.hourSlot === hour);
      setLocalRules((prev) => ({ ...prev, [hour]: String(rule?.maxCapacity ?? 0) }));
      return;
    }
    upsertMutation.mutate({ serviceId, hourSlot: hour, maxCapacity: max });
  };

  const handleExcBlur = (hour: number) => {
    const value = localExcs[hour] ?? "0";
    const max = parseInt(value, 10);
    if (isNaN(max) || max < 0) {
      const exc = (exceptions as any[]).find((e: any) => e.hourSlot === hour);
      setLocalExcs((prev) => ({ ...prev, [hour]: String(exc?.maxCapacity ?? 0) }));
      return;
    }
    if (max === 0) {
      deleteExcMutation.mutate({ serviceId, exceptionDate: selectedDate, hourSlot: hour });
    } else {
      upsertExcMutation.mutate({ serviceId, exceptionDate: selectedDate, hourSlot: hour, maxCapacity: max });
    }
  };

  // ── Derived values (no hooks below this line) ────────────────────────────
  const otherServices = services.filter((s: any) => s.id !== serviceId);

  const getRule = (hour: number) => (rules as any[]).find((r: any) => r.hourSlot === hour);
  const getAvailability = (hour: number) => (availability as any[]).find((a: any) => a.hourSlot === hour);
  const getException = (hour: number) => (exceptions as any[]).find((e: any) => e.hourSlot === hour);

  // ── Render ───────────────────────────────────────────────────────────────
  if (rulesLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="w-3 h-3 animate-spin" /> Carregando...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "rules" | "exceptions")}>
        {/* Mobile: caixa suspensa */}
        <div className="block md:hidden mb-2">
          <Select value={activeTab} onValueChange={(v) => setActiveTab(v as "rules" | "exceptions")}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rules">Regras Padrão</SelectItem>
              <SelectItem value="exceptions">Exceções por Data</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: abas normais */}
        <TabsList className="hidden md:inline-flex h-8 text-xs">
          <TabsTrigger value="rules" className="text-xs px-3 h-7">Regras Padrão</TabsTrigger>
          <TabsTrigger value="exceptions" className="text-xs px-3 h-7">
            <CalendarDays className="w-3 h-3 mr-1" /> Exceções por Data
          </TabsTrigger>
        </TabsList>

        {/* ── Aba de regras padrão ─────────────────────────────────────── */}
        <TabsContent value="rules" className="space-y-3 mt-3">
          <p className="text-xs text-muted-foreground">
            Defina quantas pessoas podem ser atendidas por faixa horária. Deixe <strong>0</strong> para sem limite.
          </p>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2">
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
            <Label htmlFor={`capacity-date-${serviceId}`} className="text-xs">Conferir ocupação em</Label>
            <Input id={`capacity-date-${serviceId}`} type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} className="h-7 w-40 text-xs" />
            <span className="text-[11px] text-muted-foreground">Os contadores abaixo usam a data selecionada.</span>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {HOURS.map((hour) => {
              const rule = getRule(hour);
              const avail = getAvailability(hour);
              const max = rule?.maxCapacity ?? 0;
              const current = avail?.current ?? 0;
              const isFull = max > 0 && current >= max;
              const pct = max > 0 ? Math.min(100, Math.round((current / max) * 100)) : 0;
              return (
                <div key={hour} className="flex flex-col items-center gap-1">
                  <span className="text-[10px] text-muted-foreground font-mono">{hour}h–{hour + 1}h</span>
                  <Input
                    type="number"
                    min={0}
                    max={999}
                    value={localRules[hour] ?? String(max)}
                    onChange={(e) => setLocalRules((prev) => ({ ...prev, [hour]: e.target.value }))}
                    onBlur={() => handleRuleBlur(hour)}
                    className={`h-8 text-center text-sm font-semibold px-1 ${
                      isFull
                        ? "border-destructive/60 bg-destructive/5"
                        : max > 0
                        ? "border-primary/50 bg-primary/5"
                        : ""
                    }`}
                  />
                  {max > 0 && avail ? (
                    <div className="w-full space-y-0.5">
                      <div className="w-full bg-muted rounded-full h-1">
                        <div
                          className={`h-1 rounded-full transition-all ${isFull ? "bg-destructive" : "bg-primary"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className={`text-[9px] font-medium ${isFull ? "text-destructive" : "text-primary"}`}>
                        {current}/{max} ocupadas
                      </span>
                    </div>
                  ) : max > 0 ? (
                    <span className="text-[9px] text-primary font-medium">máx {max}</span>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-end pt-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              disabled={resetMutation.isPending}
              onClick={() => {
                if (confirm("Resetar todos os limites para 0 (sem limite)?")) {
                  resetMutation.mutate({ serviceId });
                }
              }}
            >
              {resetMutation.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <RotateCcw className="w-3 h-3 mr-1" />
              )}
              Resetar tudo para 0
            </Button>
          </div>
          {otherServices.length > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-border/40">
              <Copy className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground shrink-0">Copiar de:</span>
              <Select value={copyFromId} onValueChange={setCopyFromId}>
                <SelectTrigger className="h-7 text-xs flex-1">
                  <SelectValue placeholder="Selecionar serviço..." />
                </SelectTrigger>
                <SelectContent>
                  {otherServices.map((s: any) => (
                    <SelectItem key={s.id} value={String(s.id)} className="text-xs">
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs px-2 shrink-0"
                disabled={!copyFromId || copyMutation.isPending}
                onClick={() =>
                  copyMutation.mutate({ fromServiceId: Number(copyFromId), toServiceId: serviceId })
                }
              >
                {copyMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                )}
                Aplicar
              </Button>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground/70">
            Horários com limite são bloqueados automaticamente quando atingem a capacidade — tanto no site quanto pelo agente de IA.
          </p>
        </TabsContent>

        {/* ── Aba de exceções por data ──────────────────────────────────── */}
        <TabsContent value="exceptions" className="space-y-3 mt-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Data da exceção:</span>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-7 text-xs w-40"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Configure limites especiais para{" "}
            <strong>
              {new Date(selectedDate + "T12:00:00").toLocaleDateString("pt-BR", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </strong>
            . Exceções têm prioridade sobre as regras padrão nesta data.
          </p>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {HOURS.map((hour) => {
              const exc = getException(hour);
              const rule = getRule(hour);
              const excMax = exc?.maxCapacity ?? 0;
              const ruleMax = rule?.maxCapacity ?? 0;
              return (
                <div key={hour} className="flex flex-col items-center gap-1">
                  <span className="text-[10px] text-muted-foreground font-mono">{hour}h–{hour + 1}h</span>
                  <Input
                    type="number"
                    min={0}
                    max={999}
                    value={localExcs[hour] ?? String(excMax)}
                    onChange={(e) => setLocalExcs((prev) => ({ ...prev, [hour]: e.target.value }))}
                    onBlur={() => handleExcBlur(hour)}
                    className={`h-8 text-center text-sm font-semibold px-1 ${
                      excMax > 0 ? "border-amber-500/60 bg-amber-500/5" : ""
                    }`}
                    placeholder={ruleMax > 0 ? String(ruleMax) : "∞"}
                  />
                  {excMax > 0 ? (
                    <span className="text-[9px] text-amber-600 font-medium">exceção: {excMax}</span>
                  ) : ruleMax > 0 ? (
                    <span className="text-[9px] text-muted-foreground/60">padrão: {ruleMax}</span>
                  ) : null}
                </div>
              );
            })}
          </div>

          {(exceptions as any[]).length > 0 && (
            <p className="text-[10px] text-amber-600/80">
              ⚡ {(exceptions as any[]).length} hora
              {(exceptions as any[]).length > 1 ? "s" : ""} com exceção configurada para esta data.
            </p>
          )}
          <p className="text-[10px] text-muted-foreground/70">
            Útil para datas especiais (Dia das Mães, feriados, promoções). Deixe <strong>0</strong> para usar a regra padrão.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
